// src/utils/dishTranslation.js

/**
 * Maps Firebase dish names to their translatable keys
 * This function converts dish names from Firebase (like "Tomato Toast") 
 * to their corresponding translation keys (like "menu.items.tomatoToast.name")
 */
export function getDishTranslationKey(dishName) {
  // Create a mapping of dish names to their translation keys
  const dishNameToKey = {
    // Appetizers
    'Tomato Toast': 'menu.items.tomatoToast.name',
    'Noodle Soup': 'menu.items.noodleSoup.name',
    'Pumpkin Soup': 'menu.items.pumpkinSoup.name',
    
    // Sushi
    'Dragon Sushi': 'menu.items.dragonSushi.name',
    'Creamy Sushi': 'menu.items.creamySushi.name',
    'Roll Salmon Sushi': 'menu.items.rollSalmonSushi.name',
    
    // Steak
    'Steak Tartare': 'menu.items.steakTartare.name',
    'Surf and Turf': 'menu.items.surfTurf.name',
    'Surf & Turf': 'menu.items.surfTurf.name',
    'Wagyu Beef': 'menu.items.wagyuBeef.name',
    
    // Chicken
    'Creamy Parmesan': 'menu.items.creamyParmesan.name',
    'Spring Chicken': 'menu.items.springChicken.name',
    'Chicken Francese': 'menu.items.chickenFrancese.name',
    
    // Beverages
    'Panthouse Tonic': 'menu.items.panthouseTonic.name',
    'Apple Breeze': 'menu.items.appleBreeze.name',
    'Frenchman Blitz': 'menu.items.frenchmanBlitz.name',
  };

  // Return the translation key if found, otherwise return the original dish name
  return dishNameToKey[dishName] || dishName;
}

/**
 * Gets the translated dish name using the i18n instance
 * @param {string} dishName - The dish name from Firebase
 * @param {Function} t - The translation function from useI18n
 * @returns {string} - The translated dish name
 * 
 * @example
 * // In a Vue component:
 * import { getTranslatedDishName } from '@/utils/dishTranslation';
 * import { useI18n } from 'vue-i18n';
 * 
 * const { t } = useI18n();
 * const translatedName = getTranslatedDishName('Tomato Toast', t);
 * // Returns: "توست بالطماطم" (in Arabic) or "Tomato Toast" (in English)
 */
export function getTranslatedDishName(dishName, t) {
  const translationKey = getDishTranslationKey(dishName);
  
  // If we have a translation key, use it; otherwise return the original name
  if (translationKey !== dishName) {
    return t(translationKey);
  }
  
  return dishName;
}

/**
 * Gets all possible dish names that can come from Firebase
 * This is useful for debugging or validation
 */
export function getAllPossibleDishNames() {
  return [
    'Tomato Toast',
    'Noodle Soup', 
    'Pumpkin Soup',
    'Dragon Sushi',
    'Creamy Sushi',
    'Roll Salmon Sushi',
    'Steak Tartare',
    'Surf and Turf',
    'Wagyu Beef',
    'Creamy Parmesan',
    'Spring Chicken',
    'Chicken Francese',
    'Panthouse Tonic',
    'Apple Breeze',
    'Frenchman Blitz'
  ];
} 